import React, { useContext, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import LinearProgress from "@mui/material/LinearProgress";
import Switch from "@mui/material/Switch";
import { useTheme } from "@mui/material/styles";
import NewTranscriptionLangDropdown from "../NewStudio/NewTranscriptionLangDropdown";
import { formatToReadableLargeTitle, LibrettoProjectTypes } from "../../utils/utils";
import { validateFileForUpload } from "../../utils/fileValidation";
import { ExtractMediaFileMetadata } from "../../storage/Media";
import { useAssetStore } from "../../storage/AssetStore";
import { AuthContext } from "../../context/AuthContext";
import { trackSessionEvent } from "../../analytics/sessionEvents";
import { db } from "../../Firebase";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import {
    kTracksCollectionName,
    TrackDeleted,
    TrackProjectId,
    WorkspaceId,
} from "../../utils/CollectionConstants";

const viewStates = {
    SELECT_FILE: "select-file",
    REVIEW_AND_RUN: "review-and-run",
    RUNNING: "running",
};

const waveformColumns = [18, 34, 26, 40, 22, 30, 38, 24, 32, 20, 36, 18, 26, 34, 28];
const imageFilePattern = /\.(bmp|jpe?g|png|gif|webp)$/i;
const transcriptionInProgressStates = new Set(["queued", "processing", "client_extracting", "pending"]);
const transcriptionFailureStates = new Set(["failed", "cancelled"]);
const transcriptionNoSpeechStates = new Set(["silent", "not_needed"]);
const transcriptionCompletedState = "completed";
const defaultEditForClaritySettings = {
    removeFillerWords: true,
    tightenSilenceGaps: true,
    removeRetakes: false,
};

const getUploadFiletype = (file) => (file?.type?.startsWith("video/") ? "video" : "audio");

const formatFileName = (name = "") => {
    const trimmed = formatToReadableLargeTitle(name || "");
    if (trimmed.length <= 74) return trimmed;
    return `${trimmed.slice(0, 74)}...`;
};

const formatMediaDuration = (durationSec) => {
    if (!Number.isFinite(durationSec) || durationSec <= 0) return "";

    const totalSeconds = Math.max(0, Math.round(durationSec));
    if (totalSeconds < 60) {
        return `${totalSeconds}s`;
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }

    return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const AudioWaveformPreview = () => {
    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                borderRadius: "12px",
                background: "linear-gradient(180deg, var(--app-bg-surface) 0%, var(--app-bg-subtle) 100%)",
                border: "1px solid var(--app-border-subtle)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                px: "26px",
            }}
        >
            {waveformColumns.map((height, idx) => (
                <Box
                    key={`${height}-${idx}`}
                    sx={{
                        width: "6px",
                        height: `${height}px`,
                        borderRadius: "999px",
                        backgroundColor: idx % 3 === 0 ? "#2B6BFD" : "var(--app-icon-primary)",
                        opacity: idx % 3 === 0 ? 0.8 : 0.65,
                    }}
                />
            ))}
        </Box>
    );
};

const MediaPreviewCard = ({ file, showMeta = false }) => {
    const [previewUrl, setPreviewUrl] = useState("");
    const [aspectRatio, setAspectRatio] = useState("landscape");
    const [durationLabel, setDurationLabel] = useState("");

    const isVideo = Boolean(file?.type?.startsWith("video/"));
    const isAudio = Boolean(file?.type?.startsWith("audio/"));

    useEffect(() => {
        if (!isVideo || !file) {
            setPreviewUrl("");
            setAspectRatio("landscape");
            if (!isAudio) {
                setDurationLabel("");
            }
            return;
        }

        let cancelled = false;
        const videoUrl = URL.createObjectURL(file);
        const video = document.createElement("video");
        video.src = videoUrl;
        video.preload = "metadata";
        video.muted = true;

        const cleanupVideoUrl = () => {
            URL.revokeObjectURL(videoUrl);
        };

        video.onloadedmetadata = () => {
            if (cancelled) return;
            const ratio = (video.videoWidth || 1) / (video.videoHeight || 1);
            if (ratio > 1.1) setAspectRatio("landscape");
            else if (ratio < 0.9) setAspectRatio("portrait");
            else setAspectRatio("square");
            setDurationLabel(formatMediaDuration(video.duration));

            const seekTarget = Number.isFinite(video.duration) && video.duration > 1 ? 1 : 0;
            const safeTarget = Math.max(0, Math.min(seekTarget, (video.duration || 0) - 0.05));
            video.currentTime = Number.isFinite(safeTarget) ? safeTarget : 0;
        };

        video.onseeked = () => {
            if (cancelled) return;
            const canvas = document.createElement("canvas");
            canvas.width = video.videoWidth || 1280;
            canvas.height = video.videoHeight || 720;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                cleanupVideoUrl();
                return;
            }
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            setPreviewUrl(canvas.toDataURL("image/jpeg", 0.85));
            cleanupVideoUrl();
        };

        video.onerror = () => {
            cleanupVideoUrl();
        };

        return () => {
            cancelled = true;
            cleanupVideoUrl();
        };
    }, [file, isVideo]);

    useEffect(() => {
        if (!isAudio || !file) {
            if (!isVideo) {
                setDurationLabel("");
            }
            return;
        }

        let cancelled = false;
        const audioUrl = URL.createObjectURL(file);
        const audio = document.createElement("audio");
        audio.src = audioUrl;
        audio.preload = "metadata";

        audio.onloadedmetadata = () => {
            if (cancelled) return;
            setDurationLabel(formatMediaDuration(audio.duration));
            URL.revokeObjectURL(audioUrl);
        };

        audio.onerror = () => {
            if (cancelled) return;
            setDurationLabel("");
            URL.revokeObjectURL(audioUrl);
        };

        return () => {
            cancelled = true;
            URL.revokeObjectURL(audioUrl);
        };
    }, [file, isAudio, isVideo]);

    const mediaStyle = (() => {
        if (aspectRatio === "portrait") {
            return {
                width: "auto",
                height: "100%",
                maxWidth: "100%",
                objectFit: "contain",
            };
        }
        if (aspectRatio === "square") {
            return {
                width: "auto",
                height: "100%",
                maxWidth: "100%",
                objectFit: "contain",
            };
        }
        return {
            width: "100%",
            height: "100%",
            objectFit: "cover",
        };
    })();

    const metaText = [formatFileName(file?.name), durationLabel].filter(Boolean).join(" \u2022 ");

    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    height: "300px",
                    borderRadius: "14px",
                    overflow: "hidden",
                    border: "1px solid var(--app-border-subtle)",
                    backgroundColor: "var(--app-bg-subtle)",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {isVideo && previewUrl ? (
                    <img src={previewUrl} alt="Uploaded video preview" style={mediaStyle} />
                ) : null}
                {isAudio ? <AudioWaveformPreview /> : null}
                {!isVideo && !isAudio ? (
                    <Box
                        sx={{
                            fontFamily: "Inter",
                            fontSize: "14px",
                            color: "var(--app-text-muted)",
                        }}
                    >
                        Preview unavailable for this file type.
                    </Box>
                ) : null}
            </Box>
            {showMeta && metaText ? (
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "var(--app-text-muted)",
                        lineHeight: "18px",
                        textAlign: "left",
                    }}
                >
                    {metaText}
                </Box>
            ) : null}
        </Box>
    );
};

const ToggleSettingRow = ({ label, enabled, onToggle, isLast = false }) => {
    return (
        <Box
            onClick={onToggle}
            sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
                px: "2px",
                py: "10px",
                borderBottom: isLast ? "none" : "1px solid var(--app-border-subtle)",
                cursor: "pointer",
                transition: "all 0.15s ease",
                "&:hover": {
                    backgroundColor: "var(--app-bg-subtle)",
                },
            }}
        >
            <Box
                sx={{
                    fontFamily: "Inter",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: enabled ? "var(--app-text-primary)" : "var(--app-text-muted)",
                    lineHeight: "16px",
                }}
            >
                {label}
            </Box>
            <Switch
                checked={enabled}
                onClick={(e) => {
                    e.stopPropagation();
                }}
                onChange={() => onToggle?.()}
                size="small"
                sx={{
                    mr: "-4px",
                    "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#2B6BFD",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: "#2B6BFD",
                        opacity: 1,
                    },
                    "& .MuiSwitch-track": {
                        backgroundColor: "var(--app-border-muted)",
                        opacity: 1,
                    },
                }}
            />
        </Box>
    );
};

const Header = ({ onClose, subtitle }) => {
    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                width: "100%",
                gap: "16px",
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", pr: "10px" }}>
                <Box
                    sx={{
                        color: "var(--app-text-primary)",
                        fontFamily: "Inter",
                        fontSize: "20px",
                        fontWeight: 600,
                        lineHeight: "28px",
                        letterSpacing: "-0.01em",
                    }}
                >
                    Clean up a recording
                </Box>
                <Box
                    sx={{
                        color: "var(--app-text-muted)",
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "20px",
                    }}
                >
                    {subtitle}
                </Box>
            </Box>
            <Box
                sx={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "10px",
                    border: "1px solid var(--app-border-subtle)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "var(--app-text-muted)",
                    "&:hover": {
                        backgroundColor: "var(--app-bg-subtle)",
                        color: "var(--app-text-primary)",
                    },
                }}
                onClick={onClose}
            >
                <CloseIcon sx={{ fontSize: "20px" }} />
            </Box>
        </Box>
    );
};

const SelectFileState = ({ onFileChoose, validationMessage, isDarkMode = false }) => {
    const inputRef = useRef(null);
    const dragDepthRef = useRef(0);
    const [isDragActive, setIsDragActive] = useState(false);

    const handleChange = async (e) => {
        const file = e?.target?.files?.[0];
        if (!file) return;
        await onFileChoose(file);
        if (e?.target) {
            e.target.value = "";
        }
    };

    const resetDragState = () => {
        dragDepthRef.current = 0;
        setIsDragActive(false);
    };

    const getDroppedFile = (e) => {
        const items = Array.from(e?.dataTransfer?.items || []);
        for (const item of items) {
            if (item?.kind !== "file") continue;
            const file = item.getAsFile?.();
            if (file) return file;
        }
        const files = e?.dataTransfer?.files;
        return files?.[0] || null;
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current += 1;
        setIsDragActive(true);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e?.dataTransfer) {
            e.dataTransfer.dropEffect = "copy";
        }
        if (!isDragActive) {
            setIsDragActive(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) {
            setIsDragActive(false);
        }
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const file = getDroppedFile(e);
        resetDragState();
        if (!file) return;
        await onFileChoose(file);
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Box
                component="label"
                htmlFor="edit-for-clarity-upload"
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                    width: "100%",
                    height: "300px",
                    borderRadius: "14px",
                    border: `1px dashed ${isDragActive ? "#2B6BFD" : "var(--app-border-muted)"}`,
                    backgroundColor: isDragActive
                        ? (isDarkMode ? "rgba(43, 107, 253, 0.18)" : "#F3F8FF")
                        : "var(--app-bg-canvas)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": {
                        backgroundColor: isDarkMode ? "rgba(43, 107, 253, 0.12)" : "var(--app-bg-subtle)",
                        borderColor: "var(--app-border-muted)",
                    },
                }}
            >
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "var(--app-text-primary)",
                        lineHeight: "20px",
                    }}
                >
                    Upload video or audio
                </Box>
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "13px",
                        fontWeight: 400,
                        color: "var(--app-text-muted)",
                        lineHeight: "18px",
                    }}
                >
                    {isDragActive ? "Drop file to upload." : "Click to browse or drag and drop your file."}
                </Box>
            </Box>
            <input
                ref={inputRef}
                id="edit-for-clarity-upload"
                type="file"
                accept="video/*, audio/*"
                hidden
                onChange={handleChange}
            />
            {validationMessage ? (
                <Box
                    sx={{
                        mt: "12px",
                        px: "12px",
                        py: "10px",
                        borderRadius: "10px",
                        border: "1px solid #FECACA",
                        backgroundColor: "#FEF2F2",
                        color: "#B91C1C",
                        fontFamily: "Inter",
                        fontSize: "12px",
                        fontWeight: 500,
                        lineHeight: "16px",
                    }}
                >
                    {validationMessage}
                </Box>
            ) : null}
        </Box>
    );
};

const RunningState = ({
    selectedFile,
    statusTitle,
    statusDetail,
    errorMessage,
    isDone,
    onChooseDifferentFile,
}) => {
    return (
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "18px" }}>
            <MediaPreviewCard file={selectedFile} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "18px",
                        fontWeight: 600,
                        color: "var(--app-text-primary)",
                        lineHeight: "24px",
                    }}
                >
                    {statusTitle}
                </Box>
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "var(--app-text-muted)",
                        lineHeight: "20px",
                    }}
                >
                    {statusDetail}
                </Box>
            </Box>
            {errorMessage ? (
                <Box
                    sx={{
                        px: "12px",
                        py: "10px",
                        borderRadius: "10px",
                        border: "1px solid #FECACA",
                        backgroundColor: "#FEF2F2",
                        color: "#B91C1C",
                        fontFamily: "Inter",
                        fontSize: "12px",
                        fontWeight: 500,
                        lineHeight: "16px",
                    }}
                >
                    {errorMessage}
                </Box>
            ) : (
                <LinearProgress
                    variant={isDone ? "determinate" : "indeterminate"}
                    value={isDone ? 100 : undefined}
                    sx={{
                        borderRadius: "999px",
                        height: "8px",
                        backgroundColor: "var(--app-border-subtle)",
                        "& .MuiLinearProgress-bar": {
                            borderRadius: "999px",
                            backgroundColor: "#2B6BFD",
                        },
                    }}
                />
            )}
            {onChooseDifferentFile ? (
                <Box
                    sx={{
                        width: "fit-content",
                        fontFamily: "Inter",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "var(--app-text-muted)",
                        cursor: "pointer",
                        "&:hover": {
                            color: "var(--app-text-primary)",
                        },
                    }}
                    onClick={onChooseDifferentFile}
                >
                    Choose a different file
                </Box>
            ) : null}
        </Box>
    );
};

const ReviewState = ({
    selectedFile,
    selectedLanguage,
    setSelectedLanguage,
    editForClaritySettings,
    onToggleSetting,
    advancedOpen,
    setAdvancedOpen,
    onRun,
    onSwapFile,
    runValidationMessage,
    isStartingRun,
    isDarkMode = false,
}) => {
    return (
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px" }}>
            <MediaPreviewCard file={selectedFile} showMeta={true} />

            <Box
                sx={{
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "var(--app-text-muted)",
                    lineHeight: "20px",
                }}
            >
                Remove filler, tighten silence, and remove retakes in one pass.
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderTop: "1px solid var(--app-border-subtle)",
                    borderBottom: "1px solid var(--app-border-subtle)",
                }}
            >
                <ToggleSettingRow
                    label="Remove filler words"
                    enabled={Boolean(editForClaritySettings?.removeFillerWords)}
                    onToggle={() => onToggleSetting("removeFillerWords")}
                    isLast={false}
                />
                <ToggleSettingRow
                    label="Tighten silence gaps"
                    enabled={Boolean(editForClaritySettings?.tightenSilenceGaps)}
                    onToggle={() => onToggleSetting("tightenSilenceGaps")}
                    isLast={false}
                />
                <ToggleSettingRow
                    label="Remove retakes"
                    enabled={Boolean(editForClaritySettings?.removeRetakes)}
                    onToggle={() => onToggleSetting("removeRetakes")}
                    isLast={true}
                />
            </Box>

            <Box
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    width: "fit-content",
                    cursor: "pointer",
                    color: "var(--app-text-muted)",
                    "&:hover": {
                        color: "var(--app-text-primary)",
                    },
                }}
                onClick={() => setAdvancedOpen(!advancedOpen)}
            >
                {advancedOpen ? (
                    <KeyboardArrowDownRoundedIcon sx={{ fontSize: "18px" }} />
                ) : (
                    <KeyboardArrowRightRoundedIcon sx={{ fontSize: "18px" }} />
                )}
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "13px",
                        fontWeight: 500,
                        lineHeight: "18px",
                    }}
                >
                    Advanced settings
                </Box>
            </Box>

            {advancedOpen ? (
                <Box
                    sx={{
                        width: "100%",
                        borderRadius: "12px",
                        border: "1px solid var(--app-border-subtle)",
                        backgroundColor: "var(--app-bg-surface)",
                        px: "14px",
                        py: "14px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                    }}
                >
                    <Box
                        sx={{
                            fontFamily: "Inter",
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "var(--app-text-primary)",
                            lineHeight: "18px",
                        }}
                    >
                        Content language
                    </Box>
                    <Box
                        sx={{
                            fontFamily: "Inter",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "var(--app-text-muted)",
                            lineHeight: "18px",
                        }}
                    >
                        Set language for speech transcription quality.
                    </Box>
                    <Box sx={{ width: "234px" }}>
                        <NewTranscriptionLangDropdown
                            selectedLanguage={selectedLanguage}
                            onChange={setSelectedLanguage}
                            compact={true}
                        />
                    </Box>
                </Box>
            ) : null}

            {runValidationMessage ? (
                <Box
                    sx={{
                        px: "12px",
                        py: "10px",
                        borderRadius: "10px",
                        border: "1px solid #FECACA",
                        backgroundColor: "#FEF2F2",
                        color: "#B91C1C",
                        fontFamily: "Inter",
                        fontSize: "12px",
                        fontWeight: 500,
                        lineHeight: "16px",
                    }}
                >
                    {runValidationMessage}
                </Box>
            ) : null}

            <Box
                sx={{
                    width: "100%",
                    height: "52px",
                    borderRadius: "999px",
                    backgroundColor: isDarkMode ? "#2B6BFD" : "#1a1a1a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: isStartingRun ? "default" : "pointer",
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                        backgroundColor: isStartingRun ? (isDarkMode ? "#2B6BFD" : "#1a1a1a") : (isDarkMode ? "#245DDB" : "#111827"),
                    },
                    opacity: isStartingRun ? 0.8 : 1,
                }}
                onClick={isStartingRun ? undefined : onRun}
            >
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#FFFFFF",
                        lineHeight: "20px",
                        letterSpacing: "0.01em",
                    }}
                >
                    {isStartingRun ? "Checking media..." : "Run cleanup"}
                </Box>
            </Box>

            <Box
                sx={{
                    width: "fit-content",
                    fontFamily: "Inter",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "var(--app-text-muted)",
                    cursor: "pointer",
                    "&:hover": {
                        color: "var(--app-text-primary)",
                    },
                }}
                onClick={onSwapFile}
            >
                Choose a different file
            </Box>
        </Box>
    );
};

const EditForClarityWorkflow = ({
    onClose,
    projectId = null,
    initialFile = null,
    onRunEditForClarity,
    onTranscriptionReady,
}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const { UploadAsset } = useAssetStore();
    const authContext = useContext(AuthContext);
    const userId = authContext.getUserId?.();
    const workspaceId =
        authContext.getCurrentWorkspaceId?.() ||
        authContext.getDefaultWorkspaceId?.() ||
        userId ||
        null;
    const [selectedFile, setSelectedFile] = useState(initialFile);
    const [selectedLanguage, setSelectedLanguage] = useState("auto");
    const [editForClaritySettings, setEditForClaritySettings] = useState({ ...defaultEditForClaritySettings });
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");
    const [isStartingRun, setIsStartingRun] = useState(false);
    const [runningTitle, setRunningTitle] = useState("Running Edit for Clarity");
    const [runningDetail, setRunningDetail] = useState("Waiting for transcription to begin.");
    const [runningError, setRunningError] = useState("");
    const [transcriptionReady, setTranscriptionReady] = useState(false);
    const [currentView, setCurrentView] = useState(
        initialFile ? viewStates.REVIEW_AND_RUN : viewStates.SELECT_FILE
    );
    const trackWatcherUnsubRef = useRef(null);
    const runSettingsRef = useRef({ ...defaultEditForClaritySettings });

    const clearTrackWatcher = () => {
        if (trackWatcherUnsubRef.current) {
            try {
                trackWatcherUnsubRef.current();
            } catch (_) {
                // no-op
            }
            trackWatcherUnsubRef.current = null;
        }
    };

    const resetRunningState = () => {
        setRunningTitle("Running Edit for Clarity");
        setRunningDetail("Waiting for transcription to begin.");
        setRunningError("");
        setTranscriptionReady(false);
        setIsStartingRun(false);
        clearTrackWatcher();
    };

    useEffect(() => {
        return () => {
            clearTrackWatcher();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (initialFile) {
            setSelectedFile(initialFile);
            setCurrentView(viewStates.REVIEW_AND_RUN);
            setValidationMessage("");
            resetRunningState();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFile]);

    const handleFileChoose = async (file) => {
        const isImageFile = Boolean(file?.type?.startsWith("image/")) || imageFilePattern.test(String(file?.name ?? ""));
        if (isImageFile) {
            setValidationMessage("Images are not supported in Edit for Clarity. Upload an audio or video file.");
            return;
        }

        const validation = await validateFileForUpload(file);
        if (validation.mode !== "local_ok") {
            const extensionText = validation.codec
                ? `${validation.extension} (${validation.codec})`
                : validation.extension;
            setValidationMessage(`${extensionText} files are not supported in this workflow.`);
            return;
        }

        setSelectedFile(file);
        setCurrentView(viewStates.REVIEW_AND_RUN);
        setValidationMessage("");
        resetRunningState();
    };

    const watchTranscriptionUntilReady = (assetId, expectedProjectId) => {
        clearTrackWatcher();
        const safeAssetId = String(assetId || "").trim();
        const safeProjectId = String(expectedProjectId || "").trim();
        if (!safeAssetId || !safeProjectId) return;

        const applyTrackSnapshot = ({ snapshot }) => {
            if (snapshot.empty) {
                setRunningTitle("Preparing transcription");
                setRunningDetail("Waiting for transcription to start.");
                return;
            }

            const docs = snapshot.docs || [];
            const trackDoc = docs.find((docSnap) => {
                const data = docSnap?.data?.() || {};
                return String(data?.assetId || "") === safeAssetId;
            });

            if (!trackDoc) {
                setRunningTitle("Preparing transcription");
                setRunningDetail("Waiting for transcription to start.");
                setRunningError("");
                setTranscriptionReady(false);
                return;
            }

            const trackData = trackDoc.data();
            const trackId = trackDoc.id;

            const transcriptionStateLower = String(trackData?.transcriptionState || "").toLowerCase();
            const transcriptId = String(trackData?.activeTranscriptId || "");
            const resolvedProjectId = String(trackData?.projectId || projectId || "");

            if (transcriptionFailureStates.has(transcriptionStateLower)) {
                setRunningTitle("Transcription failed");
                setRunningDetail("We could not transcribe this file.");
                setRunningError("Transcription failed. Please try a different file or retry later.");
                setTranscriptionReady(false);
                clearTrackWatcher();
                return;
            }

            if (transcriptionNoSpeechStates.has(transcriptionStateLower)) {
                setRunningTitle("Audio required");
                setRunningDetail("Edit for Clarity requires speech audio.");
                setRunningError("This file has no audio stream. Edit for Clarity requires audio.");
                setTranscriptionReady(false);
                clearTrackWatcher();
                return;
            }

            if (transcriptionInProgressStates.has(transcriptionStateLower)) {
                setRunningTitle("Transcribing audio");
                setRunningDetail("Analyzing speech to prepare for cleanup.");
                setRunningError("");
                setTranscriptionReady(false);
                return;
            }

            if (transcriptionStateLower === transcriptionCompletedState) {
                setRunningTitle("Transcription complete");
                setRunningDetail(onTranscriptionReady ? "Opening editor with your transcript." : "Ready to open the editor.");
                setRunningError("");
                setTranscriptionReady(true);
                clearTrackWatcher();

                if (onTranscriptionReady) {
                    const payload = {
                        assetId: safeAssetId,
                        trackId,
                        projectId: resolvedProjectId,
                        transcriptId,
                        settings: { ...runSettingsRef.current },
                    };
                    void Promise.resolve(onTranscriptionReady(payload)).catch((error) => {
                        console.error("[EditForClarityWorkflow] Failed to open editor", error);
                        setRunningTitle("Could not open editor");
                        setRunningDetail("Transcription finished, but we couldn't open the editor automatically.");
                        setRunningError("Please close this dialog and open the project manually.");
                        setTranscriptionReady(false);
                    });
                }
                return;
            }

            setRunningTitle("Preparing transcription");
            setRunningDetail("Waiting for transcription to start.");
            setRunningError("");
            setTranscriptionReady(false);
        };

        const subscribeToTrack = () => {
            const constraints = [];
            constraints.push(where(TrackProjectId, "==", safeProjectId));
            constraints.push(where(TrackDeleted, "==", false));
            if (workspaceId) {
                constraints.push(where(WorkspaceId, "==", workspaceId));
            }
            const q = query(collection(db, kTracksCollectionName), ...constraints);

            return onSnapshot(
                q,
                (snapshot) => {
                    applyTrackSnapshot({ snapshot });
                },
                (error) => {
                    const messageLower = String(error?.message || "").toLowerCase();
                    const isPrimaryLeaseIssue =
                        messageLower.includes("primary lease") || messageLower.includes("indexeddb");
                    if (isPrimaryLeaseIssue) {
                        console.warn("[EditForClarityWorkflow] Transcription watcher IndexedDB lease contention (benign):", error?.code || error);
                        return;
                    }

                    console.error("[EditForClarityWorkflow] Failed to monitor transcription status", error);
                    setRunningTitle("Transcription check failed");
                    setRunningDetail("Could not monitor transcription state.");
                    setRunningError("We could not monitor transcription status. Please try again.");
                    setTranscriptionReady(false);
                    clearTrackWatcher();
                },
            );
        };

        trackWatcherUnsubRef.current = subscribeToTrack();
    };

    const handleRun = async () => {
        if (!selectedFile || isStartingRun) return;

        runSettingsRef.current = { ...editForClaritySettings };
        trackSessionEvent("started_edit_for_clarity");
        setIsStartingRun(true);
        setValidationMessage("");
        setRunningError("");
        setTranscriptionReady(false);

        try {
            const uploadProjectId = String(projectId || "").trim();
            let uploadProjectIdResolved = uploadProjectId;
            let uploadAssetIdResolved = "";
            let hasStartedWatcher = false;

            const filetype = getUploadFiletype(selectedFile);
            const metadata = await ExtractMediaFileMetadata({
                file: selectedFile,
                fileType: filetype,
            });

            if (!metadata?.hasAudioStream) {
                setValidationMessage("This file has no audio stream. Edit for Clarity requires audio.");
                return;
            }

            setCurrentView(viewStates.RUNNING);
            setRunningTitle("Preparing transcription");
            setRunningDetail("Waiting for transcription to begin.");

            const maybeStartWatcher = () => {
                if (hasStartedWatcher) return;
                const nextAssetId = String(uploadAssetIdResolved || "").trim();
                const nextProjectId = String(uploadProjectIdResolved || uploadProjectId || "").trim();
                if (!nextAssetId || !nextProjectId) return;
                hasStartedWatcher = true;
                watchTranscriptionUntilReady(nextAssetId, nextProjectId);
            };

            const assetId = await UploadAsset({
                projectId: uploadProjectId || projectId,
                projectType: LibrettoProjectTypes.ProjectTypeCleanup,
                file: selectedFile,
                filetype,
                languageCode: selectedLanguage,
                queueEnhancementOnUpload: true,
                onUploadInitiated: ({ assetId: serverAssetId, projectId: serverProjectId }) => {
                    const resolvedAssetId = String(serverAssetId || "").trim();
                    if (resolvedAssetId) {
                        uploadAssetIdResolved = resolvedAssetId;
                    }
                    const resolved = String(serverProjectId || "").trim();
                    if (resolved) {
                        uploadProjectIdResolved = resolved;
                    }
                    maybeStartWatcher();
                },
            });

            if (!assetId) {
                throw new Error("Upload failed to return an asset id");
            }
            uploadAssetIdResolved = String(assetId || "").trim();

            if (onRunEditForClarity) {
                onRunEditForClarity({
                    file: selectedFile,
                    languageCode: selectedLanguage,
                    assetId: uploadAssetIdResolved,
                    projectId: uploadProjectIdResolved || uploadProjectId,
                    settings: { ...runSettingsRef.current },
                });
            }

            setRunningTitle("Preparing transcription");
            setRunningDetail("Waiting for transcription to begin.");
            maybeStartWatcher();
        } catch (error) {
            console.error("[EditForClarityWorkflow] Upload failed", error);
            setCurrentView(viewStates.RUNNING);
            setRunningTitle("Could not start transcription");
            setRunningDetail("We couldn't start processing this file.");
            setRunningError("Could not start processing. Please try again.");
        } finally {
            setIsStartingRun(false);
        }
    };

    const handleSwapFile = () => {
        clearTrackWatcher();
        setCurrentView(viewStates.SELECT_FILE);
        setAdvancedOpen(false);
        setValidationMessage("");
        resetRunningState();
    };

    const handleToggleSetting = (key) => {
        setEditForClaritySettings((prev) => {
            const nextValue = !Boolean(prev?.[key]);
            let eventPrefix = "";
            if (key === "removeRetakes") {
                eventPrefix = "remove_retakes";
            } else if (key === "removeFillerWords") {
                eventPrefix = "remove_filler_words";
            } else if (key === "tightenSilenceGaps") {
                eventPrefix = "tighten_silence_gaps";
            }
            if (eventPrefix) {
                trackSessionEvent(`${eventPrefix}_${nextValue ? "enabled" : "disabled"}`);
            }
            return {
                ...prev,
                [key]: nextValue,
            };
        });
    };

    const subtitle = currentView === viewStates.SELECT_FILE
        ? "Upload a file to remove filler words, tighten silences, and remove retakes."
        : "Review defaults and run in one click.";

    return (
        <Box
            sx={{
                width: "715px",
                borderRadius: "12px",
                border: "1px solid var(--app-border-subtle)",
                backgroundColor: "var(--app-bg-surface)",
                px: "24px",
                py: "24px",
                display: "flex",
                flexDirection: "column",
                gap: "20px",
            }}
        >
            <Header onClose={onClose} subtitle={subtitle} />

            {currentView === viewStates.SELECT_FILE ? (
                <SelectFileState
                    onFileChoose={handleFileChoose}
                    validationMessage={validationMessage}
                    isDarkMode={isDarkMode}
                />
            ) : null}

            {currentView === viewStates.REVIEW_AND_RUN ? (
                <ReviewState
                    selectedFile={selectedFile}
                    selectedLanguage={selectedLanguage}
                    setSelectedLanguage={setSelectedLanguage}
                    editForClaritySettings={editForClaritySettings}
                    onToggleSetting={handleToggleSetting}
                    advancedOpen={advancedOpen}
                    setAdvancedOpen={setAdvancedOpen}
                    onRun={handleRun}
                    onSwapFile={handleSwapFile}
                    runValidationMessage={validationMessage}
                    isStartingRun={isStartingRun}
                    isDarkMode={isDarkMode}
                />
            ) : null}

            {currentView === viewStates.RUNNING ? (
                <RunningState
                    selectedFile={selectedFile}
                    statusTitle={runningTitle}
                    statusDetail={runningDetail}
                    errorMessage={runningError}
                    isDone={transcriptionReady}
                    onChooseDifferentFile={handleSwapFile}
                />
            ) : null}
        </Box>
    );
};

export default EditForClarityWorkflow;
